import { AuthService, SessionInfo } from "@iotos/ims-auth-sdk/auth";

import { API_ENDPOINT } from "../../config/api";

import { ImsAppUser } from "./interfaces/user";

const IMS_ROUTES = {
  signIn: "/ims/sign-in",
  user: "/ims/me",
  signOut: "/ims/sign-out",
  loginPath: "/ims/sign-in",
} as const;

export class SessionCheckError extends Error {
  readonly status: number;

  constructor(message: string, status = 0) {
    super(message);
    this.name = "SessionCheckError";
    this.status = status;
  }

  get isForbidden(): boolean {
    return this.status === 403;
  }
}

const isImsUser = (data: unknown): data is ImsAppUser =>
  typeof data === "object" &&
  data !== null &&
  "isAuthenticated" in data &&
  ("userId" in data || "subject" in data);

const isAnonymous = (data: unknown): boolean =>
  typeof data === "object" &&
  data !== null &&
  "isAuthenticated" in data &&
  data.isAuthenticated !== true;

const isTransientFailure = (status: number): boolean => status === 0 || status >= 500;

const sessionLostListeners = new Set<() => void>();

// TODO(IMS-SDK): убрать обёртку после доработки SDK
class EkocropAuthService extends AuthService<ImsAppUser> {
  onSessionLost(listener: () => void): () => void {
    sessionLostListeners.add(listener);
    return () => {
      sessionLostListeners.delete(listener);
    };
  }

  handleUnauthorized(): void {
    if (this.isAuthenticated) {
      this.clearAuthState();
    }
  }

  async initialize(): Promise<SessionInfo<ImsAppUser>> {
    try {
      return await super.initialize();
    } catch (error) {
      this.initializationPromise = null;
      throw error;
    }
  }

  protected async loadCurrentUser(): Promise<void> {
    let response: Response;
    try {
      response = await this.config.fetchClient(
        `${this.config.baseUrl}${this.routes.user}`,
        {
          credentials: "include",
        }
      );
    } catch {
      throw new SessionCheckError("Сервер авторизации недоступен");
    }

    if (response.status === 401) {
      this.clearAuthState();
      return;
    }

    if (!response.ok) {
      if (!isTransientFailure(response.status)) {
        this.clearAuthState();
      }
      throw new SessionCheckError(
        `Проверка сессии: HTTP ${response.status}`,
        response.status
      );
    }

    const data: unknown = await response.json().catch(() => null);
    if (isAnonymous(data)) {
      this.clearAuthState();
      return;
    }
    if (!isImsUser(data)) {
      this.clearAuthState();
      throw new SessionCheckError(
        "Проверка сессии: ответ не похож на пользователя",
        response.status
      );
    }

    this.setAuthState(data);
    this.extractCsrfToken();
  }

  async signOutTo(returnPath: string): Promise<void> {
    this.clearAuthState();
    const returnUrl = encodeURIComponent(returnPath);
    this.config.redirectHandler(
      `${this.config.baseUrl}${this.routes.signOut}?returnUrl=${returnUrl}`
    );
  }
}

export const imsAuthService = new EkocropAuthService({
  baseUrl: API_ENDPOINT,
  routes: IMS_ROUTES,
  onAuthStateChange: (isAuthenticated) => {
    if (!isAuthenticated) {
      sessionLostListeners.forEach((listener) => listener());
    }
  },
});
